/**
 * Plain-INR display helpers for the Cash Book.
 *
 * Cash Book amounts are stored in plain INR (not K-INR like the points
 * ledger), so these helpers format the canonical Decimal strings directly —
 * no ×1000 conversion.
 */

/** Format a canonical Decimal string as ₹ with en-IN grouping; sign preserved. */
export function formatInr(canonical: string): string {
  if (!canonical || canonical === '0') return '₹0';
  const neg = canonical.startsWith('-');
  const abs = neg ? canonical.slice(1) : canonical;
  const dot = abs.indexOf('.');
  const intRaw = dot >= 0 ? abs.slice(0, dot) : abs;
  const frac = dot >= 0 ? abs.slice(dot + 1).replace(/0+$/, '') : '';
  const grouped = Number(intRaw || '0').toLocaleString('en-IN');
  const body = frac.length > 0 ? `${grouped}.${frac}` : grouped;
  return `${neg ? '-' : ''}₹${body}`;
}

/** Absolute numeric value of a decimal string (for sorting by magnitude). */
export function absDecimal(raw: string): number {
  if (!raw) return 0;
  const n = Number(raw.startsWith('-') ? raw.slice(1) : raw);
  return Number.isFinite(n) ? n : 0;
}

/**
 * Shareable one-line caption from a plain-INR balance. Mirrors the party
 * ledger wording ("Total due" / "Aapke" / "Settled") but uses the value
 * directly since cash balances are already plain INR.
 *
 *   balance > 0  →  "<inr> Total due"  (they owe you)
 *   balance < 0  →  "<inr> Aapke"      (you owe them)
 *   balance == 0 →  "Settled"
 */
export function buildCashCaption(balance: string): string {
  const n = Number(balance);
  if (!Number.isFinite(n) || n === 0) return 'Settled';
  const absStr = balance.startsWith('-') ? balance.slice(1) : balance;
  const inr = formatInr(absStr);
  return n > 0 ? `${inr} Total due` : `${inr} Aapke`;
}

/** Tailwind tone class from a balance sign. */
export function toneFromBalance(v: string): string {
  if (!v || v === '0') return 'text-ink-muted';
  return v.startsWith('-') ? 'text-neg' : 'text-pos';
}

/** Today's date as ISO YYYY-MM-DD (local). */
export function todayIso(): string {
  const d = new Date();
  const y = d.getFullYear().toString().padStart(4, '0');
  const m = (d.getMonth() + 1).toString().padStart(2, '0');
  const day = d.getDate().toString().padStart(2, '0');
  return `${y}-${m}-${day}`;
}

/** Shift an ISO date by `delta` days. */
export function addDaysIso(iso: string, delta: number): string {
  const [y, m, d] = iso.split('-').map((s) => parseInt(s, 10));
  const date = new Date(y!, (m ?? 1) - 1, d ?? 1);
  date.setDate(date.getDate() + delta);
  const yy = date.getFullYear().toString().padStart(4, '0');
  const mm = (date.getMonth() + 1).toString().padStart(2, '0');
  const dd = date.getDate().toString().padStart(2, '0');
  return `${yy}-${mm}-${dd}`;
}
